import React from "react";

/**
 * StatusFlag — ContractorPlus-inspired WO status pill.
 *
 * Replaces the old solid-color square pill (`bg-blue-500`, `bg-green-600`, etc.)
 * with a soft-bordered pill that has a colored dot + tinted background.
 *
 * Signature is drop-in compatible with the old `<StatusPill status={...}/>` so
 * every existing usage upgrades automatically when the file's local
 * `StatusPill` component delegates to this.
 *
 * Usage:
 *   <StatusFlag status="assigned" />
 *   <StatusFlag status="in_progress" size="sm" />
 *   <StatusFlag status="completed" label="✓ Done" />   // custom label override
 */

const MAP = {
  new:          { label: "New",         color: "#8B93A7", pulse: false },
  pool:         { label: "Pool",        color: "#F5B93F", pulse: false },
  scheduled:    { label: "Scheduled",   color: "#F97316", pulse: false },
  assigned:     { label: "Assigned",    color: "#FF6A2B", pulse: false },
  in_progress:  { label: "Active",      color: "#B12FBB", pulse: true },
  photos_in:    { label: "Photos In",   color: "#3B82F6", pulse: false },
  needs_photos: { label: "Needs Photos", color: "#3B82F6", pulse: false },
  completed:    { label: "Done",        color: "#22C6A6", pulse: false },
  canceled:     { label: "Canceled",    color: "#8B93A7", pulse: false },
  merged:       { label: "Merged",      color: "#8B93A7", pulse: false },
  follow_up:    { label: "Follow-up",   color: "#F5B93F", pulse: true },
  emergency:    { label: "Emergency",   color: "#E64C5A", pulse: true },
  parts_wait:   { label: "Parts",       color: "#8B5CF6", pulse: false },
  parts_ordered:{ label: "Parts Ordered", color: "#8B5CF6", pulse: false },
  rebilled:     { label: "Rebilled",    color: "#3B82F6", pulse: false },
  paid:         { label: "Paid",        color: "#00C4CC", pulse: false },
  quote_requested: { label: "Quote Req", color: "#F5B93F", pulse: false },
  ready_to_invoice: { label: "Ready Inv", color: "#00C4CC", pulse: false },
};

const SIZES = {
  xs: { h: "h-5", px: "px-1.5", gap: "gap-1",   dot: "h-1.5 w-1.5", text: "text-[9px]" },
  sm: { h: "h-6", px: "px-2",   gap: "gap-1.5", dot: "h-1.5 w-1.5", text: "text-[10px]" },
  md: { h: "h-7", px: "px-2.5", gap: "gap-1.5", dot: "h-2 w-2",     text: "text-[11px]" },
  lg: { h: "h-8", px: "px-3",   gap: "gap-2",   dot: "h-2 w-2",     text: "text-xs" },
};

export default function StatusFlag({ status, label, size = "sm", className = "", "data-testid": testId }) {
  const meta = MAP[status] || { label: status || "—", color: "#8B93A7", pulse: false };
  const finalLabel = label || meta.label;
  const s = SIZES[size] || SIZES.sm;

  return (
    <span
      data-testid={testId || `status-flag-${status}`}
      className={`inline-flex items-center ${s.gap} ${s.h} ${s.px} rounded-full border ${s.text} font-black uppercase tracking-widest whitespace-nowrap ${className}`}
      style={{
        borderColor: meta.color + "55",
        background: meta.color + "12",
        color: meta.color,
      }}
    >
      <span
        className={`${s.dot} rounded-full flex-shrink-0 ${meta.pulse ? "animate-pulse" : ""}`}
        style={{ background: meta.color }}
      />
      <span>{finalLabel}</span>
    </span>
  );
}

/** Named export for the color map so callers can style compatible elements. */
export const STATUS_COLORS = Object.fromEntries(
  Object.entries(MAP).map(([k, v]) => [k, v.color])
);
